import {
  Box,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { HolidayCalendarSelector } from 'components/settings/components/sections/openingHours/components/holidayCalendarSelector/holidayCalendarSelector';
import { getExceptionsFromHolidays } from 'utils/holidays';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import {
  ModifiedListItem,
  useScheduleContext,
} from '../../../../schedule.context';

type Props = {
  timezone: string;
  onClose: VoidFunction;
  onSubmit: (modifiedListItems: ModifiedListItem, countryCode?: string) => void;
};

type State = {
  countryCode: null | string;
  isClosed: boolean;
};

export const ImportHolidaysModal = ({
  timezone,
  onClose,
  onSubmit,
}: Props): JSX.Element => {
  const [{ isClosed, countryCode }, setState] = useState<State>({
    countryCode: null,
    isClosed: false,
  });

  const { holidays, getHolidays } = useScheduleContext();

  const { user } = useCurrentUser();
  const agentName = getUserDisplayName(user);

  useUpdateEffect(() => {
    if (!!holidays.length && countryCode) {
      const modifiedItems = getExceptionsFromHolidays({
        agentName,
        holidays: holidays,
        isClosed,
      }).reduce((acc, holiday) => ({ ...acc, [holiday.id]: holiday }), {});

      onSubmit(modifiedItems, countryCode);
      onClose();
    }
  }, [holidays]);

  const handleCalendarChange = useCallback(
    (countryCode: null | string, isClosed: boolean) => {
      if (countryCode) {
        setState({ countryCode, isClosed });
      }
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (countryCode) {
      ampli.businessHoursScheduleImportHolidayFormSubmitted({
        countryCode,
        isClosed,
      });
      getHolidays(countryCode, timezone);
    }
  }, [countryCode, isClosed, timezone, getHolidays]);

  const handleCancelModal = useCallback(() => {
    ampli.businessHoursScheduleImportHolidayFormCancelled();
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen onClose={handleCancelModal}>
      <Box width="600px">
        <ModalHead
          onClose={handleCancelModal}
          title="Connect holiday calendar"
        />
        <ModalBody>
          <HolidayCalendarSelector onChange={handleCalendarChange} />
        </ModalBody>
        <ModalFoot
          cancelOnClick={handleCancelModal}
          confirmProps={{ disabled: !countryCode }}
          confirmName="Update"
          confirmOnClick={handleSubmit}
        />
      </Box>
    </Modal>
  );
};
